import React, { useContext, useMemo, useState } from 'react'
import I18n from 'i18n'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavLink, useHistory } from 'react-router-dom'
import { SessionContext } from '../../../session/SessionProvider'
import { useGroupsQuery } from '../../../backend/Queries'
import Routes from '../../../common/Routes'
import type Group from '../../../common/types/Group'

interface SidebarItem {
  id: string
  label: string
  to: string | null
}

const WAH_RESEARCH_GROUP_NAME = 'WAH Research Group'

const ResearchDashboardSidebar: React.FC = () => {
  const { myProfile } = useContext(SessionContext)
  const history = useHistory()
  const { data: groups } = useGroupsQuery()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)

  const questionnairesGroup: Group | undefined = useMemo(
    () => groups?.find(group => group.name.trim().toLowerCase() === WAH_RESEARCH_GROUP_NAME.toLowerCase()),
    [groups]
  )

  const questionnairesPath = useMemo(() => {
    if (groups == null) return null
    if (questionnairesGroup != null) return Routes.network.groups.showFn(String(questionnairesGroup.id))
    return Routes.network.groups.index
  }, [groups, questionnairesGroup])

  const sidebarItems: SidebarItem[] = [
    { id: 'watchAtHeart', label: 'Watch@Heart', to: Routes.analysis.researchDashboard.index },
    { id: 'questionnaires', label: I18n.t('components.dashboards.research_dashboard.questionnaires_title'), to: questionnairesPath }
  ]

  const displayName = myProfile != null
    ? `${myProfile.first_name} ${myProfile.last_name}`.trim()
    : ''

  const researcherRole = String(I18n.t('components.dashboards.research_dashboard.researcher_role'))

  const profileSubtitle = myProfile?.organization != null && myProfile.organization.trim() !== ''
    ? `${researcherRole} ${myProfile.organization.trim()}`
    : researcherRole

  const handleProfileClick = (): void => {
    if (myProfile?.slug == null || myProfile.slug.trim() === '') return
    history.push(Routes.profiles.showFn(myProfile.slug))
  }

  const handleMobileBackClick = (): void => {
    setIsMobileMenuOpen(false)
    handleBackClick()
  }

  const handleMobileProfileClick = (): void => {
    setIsMobileMenuOpen(false)
    handleProfileClick()
  }

  const handleBackClick = (): void => {
    if (history.replace != null) {
      history.replace(Routes.analysis.index)
      return
    }
    history.push(Routes.analysis.index)
  }

  return (
    <nav className={`rd-sidebar ${isMobileMenuOpen ? 'rd-sidebar-mobile-open' : ''}`} aria-label='Research Dashboard navigation'>
      <div className='rd-sidebar-top'>
        <button
          type='button'
          className='rd-sidebar-back-button'
          onClick={handleBackClick}
          aria-label='Back'
        >
          <FontAwesomeIcon icon='chevron-left' className='rd-sidebar-back-icon' />
          <span>{I18n.t('back_button')}</span>
        </button>

        <div className='rd-sidebar-title'>
          <FontAwesomeIcon icon='search' className='rd-sidebar-title-icon' />
          <span>{I18n.t('components.dashboards.research_dashboard.name')}</span>
        </div>

        <button
          type='button'
          className='rd-sidebar-mobile-title-toggle'
          onClick={() => setIsMobileMenuOpen(prev => !prev)}
          aria-expanded={isMobileMenuOpen}
          aria-controls='rd-sidebar-mobile-menu'
        >
          <span>{I18n.t('components.dashboards.research_dashboard.name')}</span>
          <FontAwesomeIcon icon={isMobileMenuOpen ? 'chevron-up' : 'chevron-down'} />
        </button>

        <ul className='rd-sidebar-menu'>
          {sidebarItems.map(item => (
            <li key={item.id}>
              {item.to == null
                ? (
                  <span className='rd-sidebar-menu-item rd-sidebar-menu-item-disabled'>{item.label}</span>
                  )
                : (
                  <NavLink
                    to={item.to}
                    className='rd-sidebar-menu-item'
                    activeClassName='rd-sidebar-menu-item-active'
                    exact={item.id === 'watchAtHeart'}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                  )}
            </li>
          ))}
        </ul>

        <div
          id='rd-sidebar-mobile-menu'
          className={`rd-sidebar-menu-mobile ${isMobileMenuOpen ? 'open' : ''}`}
        >
          <button
            type='button'
            className='rd-sidebar-menu-item rd-sidebar-menu-item-utility'
            onClick={handleMobileBackClick}
          >
            {I18n.t('back_button')}
          </button>

          {sidebarItems.map(item => (
            item.to == null
              ? (
                <span key={item.id} className='rd-sidebar-menu-item rd-sidebar-menu-item-disabled'>{item.label}</span>
                )
              : (
                <NavLink
                  key={item.id}
                  to={item.to}
                  className='rd-sidebar-menu-item'
                  activeClassName='rd-sidebar-menu-item-active'
                  exact={item.id === 'watchAtHeart'}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
                )
          ))}

          {myProfile != null && (
            <button
              type='button'
              className='rd-sidebar-menu-item rd-sidebar-menu-item-utility'
              onClick={handleMobileProfileClick}
            >
              {displayName !== '' ? displayName : I18n.t('components.dashboards.research_dashboard.profile')}
            </button>
          )}
        </div>
      </div>

      {myProfile != null && (
        <button
          type='button'
          className='rd-sidebar-user rd-sidebar-user-button'
          onClick={handleProfileClick}
          aria-label='Open my profile'
        >
          {myProfile.picture != null && myProfile.picture !== ''
            ? (
              <img
                className='rd-sidebar-user-avatar'
                src={myProfile.picture}
                alt={displayName}
              />
              )
            : (
              <div className='rd-sidebar-user-avatar rd-sidebar-user-avatar-placeholder'>
                <FontAwesomeIcon icon='user' />
              </div>
              )}
          <div className='rd-sidebar-user-info'>
            <span className='rd-sidebar-user-name'>{displayName}</span>
            <span className='rd-sidebar-user-role'>{profileSubtitle}</span>
          </div>
        </button>
      )}
    </nav>
  )
}

export default ResearchDashboardSidebar
